import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { PanelLeftClose, PanelLeftOpen, X } from "lucide-react";
import { getVisibleNavigation, isNavPathActive } from "../../navigation/adminNavConfig";
import SidebarGroup from "./SidebarGroup";
import SidebarNavItem from "./SidebarNavItem";

const COLLAPSED_KEY = "adminSidebarCollapsed";
const GROUPS_KEY = "adminSidebarOpenGroups";

function readCollapsed() {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === "1";
  } catch (_) {
    return false;
  }
}

function writeCollapsed(collapsed) {
  try {
    localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0");
  } catch (_) {}
}

function readGroups() {
  try {
    const parsed = JSON.parse(localStorage.getItem(GROUPS_KEY) || "[]");
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch (_) {
    return new Set();
  }
}

function writeGroups(groups) {
  try {
    localStorage.setItem(GROUPS_KEY, JSON.stringify([...groups]));
  } catch (_) {}
}

export default function AdminSidebar({ role, mobileOpen, onClose, badges = {}, hrAccess = null, permissions = null }) {
  const location = useLocation();
  const items = useMemo(() => getVisibleNavigation(role, hrAccess, { permissions }), [role, hrAccess, permissions]);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [openGroups, setOpenGroups] = useState(readGroups);
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window.matchMedia === "function" ? window.matchMedia("(min-width: 1024px)").matches : true
  );
  const labelsVisible = mobileOpen || !collapsed;
  const drawerClosed = !isDesktop && !mobileOpen;

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const media = window.matchMedia("(min-width: 1024px)");
    const onChange = () => setIsDesktop(media.matches);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    const activeGroup = items.find((item) =>
      item.children?.some((child) => isNavPathActive(location.pathname, child))
    );
    if (!activeGroup) return;
    setOpenGroups((prev) => {
      if (prev.has(activeGroup.id)) return prev;
      const next = new Set(prev);
      next.add(activeGroup.id);
      writeGroups(next);
      return next;
    });
  }, [items, location.pathname]);

  const setCollapsedPersisted = (next) => {
    setCollapsed(next);
    writeCollapsed(next);
  };

  const toggleGroup = (id) => {
    if (collapsed && !mobileOpen) {
      setCollapsedPersisted(false);
      setOpenGroups((prev) => {
        const next = new Set(prev);
        next.add(id);
        writeGroups(next);
        return next;
      });
      return;
    }

    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      writeGroups(next);
      return next;
    });
  };

  return (
    <aside
      id="admin-sidebar"
      data-collapsed={collapsed ? "true" : "false"}
      aria-hidden={drawerClosed || undefined}
      inert={drawerClosed ? true : undefined}
      className={`
        fixed inset-y-0 left-0 z-50 flex h-full w-[280px] shrink-0 flex-col border-r border-slate-200 bg-white shadow-xl
        transition-transform duration-300 ease-in-out
        lg:static lg:z-auto lg:translate-x-0 lg:shadow-none lg:transition-[width]
        ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
        ${collapsed ? "lg:w-[72px]" : "lg:w-[280px]"}
      `}
    >
      <div className={`flex items-center gap-2 border-b border-slate-100 px-3 py-3 ${labelsVisible ? "justify-between" : "justify-center"}`}>
        <p className={`truncate text-sm font-bold text-slate-800 ${labelsVisible ? "" : "sr-only"}`}>القائمة</p>
        <button
          type="button"
          className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 lg:hidden"
          onClick={onClose}
          aria-label="إغلاق القائمة"
        >
          <X size={18} />
        </button>
        <button
          type="button"
          className="hidden h-9 w-9 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 lg:inline-flex"
          onClick={() => setCollapsedPersisted(!collapsed)}
          aria-label={collapsed ? "توسيع القائمة" : "طي القائمة"}
        >
          {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
        </button>
      </div>

      <nav aria-label="التنقل الرئيسي" className="flex-1 space-y-1 overflow-y-auto overflow-x-hidden px-2 py-3">
        {items.map((item) =>
          item.children ? (
            <SidebarGroup
              key={item.id}
              group={item}
              open={openGroups.has(item.id)}
              onToggle={() => toggleGroup(item.id)}
              labelsVisible={labelsVisible}
              badges={badges}
              pathname={location.pathname}
              onNavigate={onClose}
            />
          ) : (
            <SidebarNavItem
              key={item.id}
              item={item}
              active={isNavPathActive(location.pathname, item)}
              labelsVisible={labelsVisible}
              onNavigate={onClose}
            />
          )
        )}
      </nav>
    </aside>
  );
}
